import { Routes, Route, Navigate } from 'react-router-dom'
import { SyncStatusProvider } from './contexts/SyncStatusContext'
import { useAuth } from './AuthProvider'
import SignIn from './pages/SignIn/SignIn'
import Register from './pages/Register/Register'
import ConsultantHome from './pages/ConsultantHome/ConsultantHome'
import CustomerList from './pages/CustomerList/CustomerList'
import CustomerPage from './pages/CustomerPage/CustomerPage'
import CustomerEdit from './pages/CustomerEdit/CustomerEdit'
import NewCustomer from './pages/NewCustomer/NewCustomer'
import DataManagement from './pages/DataManagement/DataManagement'
import Connectors from './pages/Connectors/Connectors'
import Registers from './pages/Registers/Registers'
import Documents from './pages/Documents/Documents'
import ValueMappings from './pages/ValueMappings/ValueMappings'
import AnalysisHistory from './pages/AnalysisHistory/AnalysisHistory'
import AnalysisResults from './pages/AnalysisResults/AnalysisResults'
import WorkEffort from './pages/WorkEffort/WorkEffort'
import CustomerPortal from './pages/CustomerPortal/CustomerPortal'
// WorkBench removed — WorkEffort handles all question types now
import Workplan from './pages/Workplan/Workplan'
import ThreadLibrary from './pages/ThreadLibrary/ThreadLibrary'
import ThreadDetail from './pages/ThreadDetail/ThreadDetail'
import ThreadExecution from './pages/ThreadExecution/ThreadExecution'
import ExportCenter from './pages/ExportCenter/ExportCenter'
import ExportTemplates from './pages/ExportTemplates/ExportTemplates'
import AdminDashboard from './pages/AdminDashboard/AdminDashboard'
import QueryInspector from './pages/QueryInspector/QueryInspector'
import MemoryViewer from './pages/MemoryViewer/MemoryViewer'
import ContextGraph from './pages/ContextGraph/ContextGraph'
import AdminPaycodeCrosswalk from './pages/AdminPaycodeCrosswalk/AdminPaycodeCrosswalk'
import VendorLibrary from './pages/VendorLibrary/VendorLibrary'
import TemplateLibrary from './pages/TemplateLibrary/TemplateLibrary'
import ComplianceRules from './pages/ComplianceRules/ComplianceRules'
import AdminConnectors from './pages/AdminConnectors/AdminConnectors'
import AdminOnboarding from './pages/AdminOnboarding/AdminOnboarding'
import AdminSchemaDiff from './pages/AdminSchemaDiff/AdminSchemaDiff'
import AdminDevHubCapture from './pages/AdminDevHubCapture/AdminDevHubCapture'
import KnowledgeBase from './pages/KnowledgeBase/KnowledgeBase'
import AdminObservations from './pages/AdminObservations/AdminObservations'
import AdminKnowledge from './pages/AdminKnowledge/AdminKnowledge'
import SecurityManagement from './pages/SecurityManagement/SecurityManagement'
import RoleConfiguration from './pages/RoleConfiguration/RoleConfiguration'
import MFASetup from './pages/MFASetup/MFASetup'

// ─── Protected Route Wrapper ─────────────────────────────────
// Redirects to landing/login if not authenticated.
// Redirects to /security/mfa while a staff member still owes TOTP enrollment -- the
// server refuses that session everywhere else with 403 mfa_required anyway.
// Sends customer-role users to their own portal (the role matrix gives them nothing else).
// Shows a loading spinner while auth is initializing.
function RootRoute() {
  const { loading } = useAuth()
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0a0a0a' }}>
      <div style={{ width:32, height:32, border:'3px solid #1a2e1a', borderTopColor:'#031244', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
    </div>
  )
  // The front door is sign-in; a signed-in user is sent on to their home (SignIn.homeFor).
  return <SignIn />
}

function PortalRoute({ children }) {
  const { isAuthenticated, loading } = useAuth()
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}>
      <div style={{ width:28, height:28, border:'3px solid #e5e7eb', borderTopColor:'#031244', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
    </div>
  )
  if (!isAuthenticated) return <Navigate to="/" replace />
  return children
}

function Protected({ children, requireAdmin = false }) {
  const { isAuthenticated, isAdmin, isCustomer, needsMFASetup, profile, loading } = useAuth()

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: 'var(--font-sans)', color: '#6b7280',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 28, height: 28, border: '3px solid #e5e7eb',
            borderTopColor: '#031244', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
          }} />
          Loading...
        </div>
      </div>
    )
  }

  if (!isAuthenticated) return <Navigate to="/" replace />
  if (needsMFASetup) return <Navigate to="/security/mfa" replace />
  if (isCustomer) {
    const own = profile?.customer_ids?.[0]
    return <Navigate to={own ? '/portal' : '/'} replace />
  }
  if (requireAdmin && !isAdmin) return <Navigate to="/home" replace />
  return children
}

// Signed in, MFA not required to be finished yet: only the MFA setup page uses this.
function SignedIn({ children }) {
  const { isAuthenticated, loading } = useAuth()
  if (loading) return null
  if (!isAuthenticated) return <Navigate to="/" replace />
  return children
}

// ─── Portal Route Wrapper ────────────────────────────────────
// For /portal/:customerId — accessible by:
//   - role === 'customer' (their own portal)
//   - role === 'consultant' or 'admin' (preview mode)
// Customers who hit any other route get redirected here.
// /portal (no id) is the customer's own portal -- the URL customers are sent to.
function OwnPortal() {
  const { profile, isCustomer } = useAuth()
  const own = profile?.customer_ids?.[0]
  if (!isCustomer) return <Navigate to="/customers" replace />
  if (!own) return (
    <div style={{ padding: 48, textAlign: 'center', fontFamily: 'var(--font-sans)', color: '#6b7280' }}>
      Your account isn't linked to a company yet. Contact your Sempirical Account Representative.
    </div>
  )
  return <CustomerPortal />
}

export default function App() {
  return (
    <SyncStatusProvider>
    <Routes>
      {/* ── A: Auth & Landing (public) ─────────────── */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<RootRoute />} />
      {/* The marketing pages were retired 2026-10-03; their old links land on sign-in. */}
      <Route path="/enterprise" element={<Navigate to="/" replace />} />
      <Route path="/health-check" element={<Navigate to="/" replace />} />
      <Route path="/investors" element={<Navigate to="/" replace />} />
      {/* No self-registration (invite-only). /invite and /reset-password are where the
          invite / reset emails land: set your own password, then sign in. */}
      <Route path="/register" element={<Navigate to="/" replace />} />
      <Route path="/invite" element={<Register />} />
      <Route path="/reset-password" element={<Register />} />
      <Route path="/security/mfa" element={<SignedIn><MFASetup /></SignedIn>} />

      {/* ── B: Home & Customers ────────────────────── */}
      <Route path="/home" element={<Protected><ConsultantHome /></Protected>} />
      {/* WorkBench removed — WorkEffort handles all question types */}
      <Route path="/customers" element={<Protected><CustomerList /></Protected>} />
      <Route path="/customers/new" element={<Protected><NewCustomer /></Protected>} />
      <Route path="/customers/:customerId/edit" element={<Protected><CustomerEdit /></Protected>} />
      <Route path="/customers/:customerId/profile" element={<Protected><CustomerPage /></Protected>} />
      <Route path="/customers/:customerId/data" element={<Protected><DataManagement /></Protected>} />
      <Route path="/customers/:customerId/connectors" element={<Protected><Connectors /></Protected>} />
      <Route path="/customers/:customerId/registers" element={<Protected><Registers /></Protected>} />
      <Route path="/customers/:customerId/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/customers/:customerId/mappings" element={<Protected><ValueMappings /></Protected>} />

      {/* ── D: Workspace ───────────────────────────── */}
      <Route path="/customers/:customerId/work-efforts/:effortId" element={<Protected><WorkEffort /></Protected>} />

      {/* ── E: Analysis ────────────────────────────── */}
      <Route path="/customers/:customerId/analysis" element={<Protected><AnalysisHistory /></Protected>} />
      <Route path="/customers/:customerId/analysis/:analysisId" element={<Protected><AnalysisResults /></Protected>} />

      {/* ── F: Workplan ────────────────────────────── */}
      <Route path="/customers/:customerId/workplan/:workplanId" element={<Protected><Workplan /></Protected>} />

      {/* ── G: Exports ─────────────────────────────── */}
      <Route path="/customers/:customerId/exports" element={<Protected><ExportCenter /></Protected>} />

      {/* Redirects for legacy routes */}
      <Route path="/customers/:customerId/dashboard" element={<Protected><CustomerPage /></Protected>} />

      {/* Unified Customer Page */}
      <Route path="/customers/:customerId" element={<Protected><CustomerPage /></Protected>} />

      {/* ── Customer Portal (customer role + BD + consultant preview) ── */}
      <Route path="/portal" element={<PortalRoute><OwnPortal /></PortalRoute>} />
      <Route path="/portal/:customerId" element={<PortalRoute><CustomerPortal /></PortalRoute>} />

      {/* ── H: Threads ─────────────────────────────── */}
      <Route path="/threads" element={<Protected><ThreadLibrary /></Protected>} />
      <Route path="/threads/:threadId" element={<Protected><ThreadDetail /></Protected>} />
      <Route path="/threads/:threadId/run" element={<Protected><ThreadExecution /></Protected>} />
      <Route path="/threads/:threadId/run/:executionId" element={<Protected><ThreadExecution /></Protected>} />

      {/* ── G: Exports (global) ────────────────────── */}
      <Route path="/exports" element={<Protected><ExportCenter /></Protected>} />
      <Route path="/exports/templates" element={<Protected><ExportTemplates /></Protected>} />
      <Route path="/exports/templates/:templateId" element={<Protected><ExportTemplates /></Protected>} />

      {/* ── I: Admin — System (admin only) ─────────── */}
      <Route path="/admin" element={<Protected requireAdmin><AdminDashboard /></Protected>} />
      <Route path="/admin/security" element={<Protected requireAdmin><SecurityManagement /></Protected>} />
      <Route path="/admin/roles" element={<Protected requireAdmin><RoleConfiguration /></Protected>} />
      <Route path="/admin/query" element={<Protected requireAdmin><QueryInspector /></Protected>} />
      <Route path="/admin/memory" element={<Protected requireAdmin><MemoryViewer /></Protected>} />
      <Route path="/admin/graph" element={<Protected requireAdmin><ContextGraph /></Protected>} />
      <Route path="/admin/paycode-crosswalk" element={<Protected requireAdmin><AdminPaycodeCrosswalk /></Protected>} />
      {/* Contracts moved to each customer's page (Contract tab, shown as the customer sees it); old bookmarks land on the customer list. */}
      <Route path="/admin/contracts" element={<Navigate to="/customers" replace />} />
      <Route path="/admin/dev-hub-capture" element={<Protected requireAdmin><AdminDevHubCapture /></Protected>} />

      {/* ── J: Admin — Content (admin only) ────────── */}
      <Route path="/admin/templates" element={<Protected requireAdmin><TemplateLibrary /></Protected>} />
      <Route path="/admin/vendors" element={<Protected requireAdmin><VendorLibrary /></Protected>} />
      <Route path="/admin/compliance" element={<Protected requireAdmin><ComplianceRules /></Protected>} />
      <Route path="/admin/connectors" element={<Protected requireAdmin><AdminConnectors /></Protected>} />
      <Route path="/admin/onboarding" element={<Protected requireAdmin><AdminOnboarding /></Protected>} />
      <Route path="/admin/schema-diff" element={<Protected requireAdmin><AdminSchemaDiff /></Protected>} />
      <Route path="/admin/knowledge" element={<Protected requireAdmin><KnowledgeBase /></Protected>} />
      <Route path="/admin/observations" element={<Protected requireAdmin><AdminObservations /></Protected>} />
        <Route path="/admin/rules" element={<Protected requireAdmin><AdminKnowledge /></Protected>} />

      {/* ── Fallback ───────────────────────────────── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </SyncStatusProvider>
  )
}


